/** @jsxImportSource theme-ui */
import React from 'react';
import { useSelector } from 'react-redux';

import { InsightsState } from 'app-redux/InsightsState';
import { PreventabilityDisruptionFactorType } from 'client/dto';

import { BdPreventabilityIcon } from 'components/_common/Icon/BdPreventabilityIcon';

import diseasesSelectors from 'diseases/diseases.selectors';
import riskOverviewSelectors from 'riskOverview/riskOverview.selectors';

import RiskOverviewDetailColumnListItem from './RiskOverviewDetailColumnListItem';
import RiskOverviewDetailColumnNoDataListItem from './RiskOverviewDetailColumnNoDataListItem';
import {
  detailColumnListStyle,
  disruptionFactorSectionStyles,
  iconColumnStyle,
} from './RiskOverviewStyles';

type RiskOverviewCardPreventabilityDetailsProps = {
  diseaseActivityId: number;
  locationId: number;
};

const RiskOverviewCardPreventabilityDetails: React.FC<
  RiskOverviewCardPreventabilityDetailsProps
> = ({ diseaseActivityId, locationId }) => {
  const preventabilityType = useSelector<InsightsState, PreventabilityDisruptionFactorType>(
    riskOverviewSelectors.getPreventabilityByDiseaseActivityIdAndLocationId(
      diseaseActivityId,
      locationId
    )
  );
  const diseaseId = useSelector<InsightsState, number>(
    riskOverviewSelectors.getDiseaseIdByActivityIdAndLocationId(diseaseActivityId, locationId)
  );
  const diseaseName = useSelector<InsightsState, string>(
    diseasesSelectors.getDiseaseName(diseaseId)
  );

  const details: string[] = getDetails(preventabilityType, diseaseName);

  return (
    <div
      data-testid="Risk-Overview-Preventability-Panel"
      sx={disruptionFactorSectionStyles}
      className="ro-card-disrupt-factor-container"
    >
      <div sx={iconColumnStyle} className="icon-column">
        <BdPreventabilityIcon type={preventabilityType} />
      </div>
      <ul sx={detailColumnListStyle} className="detail-column">
        {details.map((detail, index) => {
          return <RiskOverviewDetailColumnListItem key={index} itemText={detail} />;
        })}
        {details.length === 0 && <RiskOverviewDetailColumnNoDataListItem />}
      </ul>
    </div>
  );
};

const getDetails = (type: PreventabilityDisruptionFactorType, diseaseName: string) => {
  switch (type) {
    case PreventabilityDisruptionFactorType.WideProphylaxis:
      return [`There is wide commercial prophylaxis availability to prevent ${diseaseName}`];
    case PreventabilityDisruptionFactorType.WideVaccine:
      return [`There is wide commercial vaccine availability to prevent ${diseaseName}`];
    case PreventabilityDisruptionFactorType.LimitedVaccine:
      return [
        `The vaccine for ${diseaseName} is available, but is limited to high risk occupations or groups`,
      ];
    case PreventabilityDisruptionFactorType.NoVaccineOrProphylaxis:
      return [`There is no vaccine or prophylaxis available to prevent ${diseaseName}`];
    default:
      return [];
  }
};

export default RiskOverviewCardPreventabilityDetails;
